import { fetchQueries } from './Query';
import { fetchBookings } from './Booking';
import { fetchFollowupsForToday } from './Followup';
import { fetchCustomers } from './Customer';

export type DashboardData = {
  queries: any[];
  recentQueries: any[];
  queriesOverTime: Array<{ date: string; count: number }>;
  queriesByType?: Array<{ type: string; count: number }>;
  queriesByAssignee?: Array<{ assigneeId: number | string; assigneeName: string; count: number }>;
  followups: any[];
  bookings: any[];
  paymentsReceived: Array<{ date: string; customer: string; amount: number }>;
  paymentsPending: Array<{ date: string; customer: string; amount: number }>;
  bookingsByDate: Array<{ date: string; type: string; amount: number }>;
  departures: Array<{ title: string; date: string; customer: string }>;
  stats: {
    customersTotal: number;
    customersNew30: number;
    queriesTotal: number;
    queriesNew30: number;
    followupsToday: number;
    bookedAmt30: number;
    paidAmt30: number;
  };
};

function ymdLocal(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export type DashboardFilters = {
  queryType?: string;
  assignee?: string;
  month?: '' | 'prev' | 'last3' | 'ytd';
  from?: string; // YYYY-MM-DD
  to?: string;   // YYYY-MM-DD
  tz?: string;
  byDateDays?: number;
  paymentsLimit?: number;
};

function monthToRange(month?: '' | 'prev' | 'last3' | 'ytd', tz?: string): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const to = ymdLocal(now);
  const firstOfMonth = new Date(y, m, 1);
  if (!month) return { from: ymdLocal(firstOfMonth), to };
  if (month === 'prev') {
    const prevFirst = new Date(y, m - 1, 1);
    const prevLast = new Date(y, m, 0);
    return { from: ymdLocal(prevFirst), to: ymdLocal(prevLast) };
    }
  if (month === 'last3') {
    const from = new Date(y, m - 2, 1);
    return { from: ymdLocal(from), to };
  }
  if (month === 'ytd') {
    const from = new Date(y, 0, 1);
    return { from: ymdLocal(from), to };
  }
  return { from: ymdLocal(firstOfMonth), to };
}

function withParams(base: string, params: Record<string, any>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    usp.set(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${base}?${qs}` : base;
}

export async function fetchDashboardData(filters?: DashboardFilters): Promise<DashboardData> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const TRAVEL_BASE_URL = process.env.REACT_APP_TRAVEL_API_BASE_URL;
  const tz = filters?.tz || 'UTC';
  const range = filters?.from && filters?.to ? { from: filters.from, to: filters.to } : monthToRange(filters?.month, tz);
  const byDateDays = filters?.byDateDays ?? 7;
  const paymentsLimit = filters?.paymentsLimit ?? 7;

  // 1) Try separate dashboard endpoints and merge
  try {
    let qRaw: any = null;
    let tRaw: any = null;
    // Query backend dashboard
    if (API_BASE_URL) {
      const qUrl = withParams(`${API_BASE_URL}/api/dashboard`, {
        tz,
        from: range.from,
        to: range.to,
        queryType: filters?.queryType,
        assignee: filters?.assignee,
      });
      const r = await fetch(qUrl, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (r.ok) qRaw = await r.json();
    }
    // Travel backend dashboard (try a few likely paths)
    if (TRAVEL_BASE_URL) {
      const candidates = [`${TRAVEL_BASE_URL}/dashboard`, `${TRAVEL_BASE_URL}/bookings/dashboard`, `${TRAVEL_BASE_URL}/bookings/summary`];
      for (const base of candidates) {
        const url = withParams(base, { tz, from: range.from, to: range.to, byDateDays, paymentsLimit });
        try {
          const r = await fetch(url, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
          if (r.ok) { tRaw = await r.json(); break; }
        } catch {}
      }
    }

    if (qRaw || tRaw) {
      // Normalize Queries/Customers/Followups side
      const qQueries = (qRaw?.queries || qRaw?.recent_queries || []) as any[];
      const qFollowups = (qRaw?.followups || qRaw?.today_followups || []) as any[];
      const qStats = qRaw?.stats || {};
      let queriesOverTime = qRaw?.queriesOverTime || qRaw?.queries_over_time || [];
      if ((!queriesOverTime || queriesOverTime.length === 0) && Array.isArray(qQueries)) {
        const now = new Date(); const start = new Date(now); start.setDate(now.getDate() - 29);
        const buckets: Record<string, number> = {}; for (let i=0;i<30;i++){ const d = new Date(start); d.setDate(start.getDate()+i); buckets[ymdLocal(d)] = 0; }
        qQueries.forEach(q => { const ds = q.queryDate || q.query_date || q.created_at || q.createdAt; if (!ds) return; const k = ymdLocal(new Date(ds)); if (k in buckets) buckets[k] += 1; });
        queriesOverTime = Object.keys(buckets).map(k => ({ date: k, count: buckets[k] }));
      }
      const recentQueries = (qRaw?.recentQueries || qRaw?.recent_queries) ?? (Array.isArray(qQueries) ? qQueries.slice().sort((a:any,b:any)=> new Date(b.queryDate||b.created_at).getTime() - new Date(a.queryDate||a.created_at).getTime()).slice(0,7) : []);

      // By-type and by-assignee summaries if provided
      const queriesByType = qRaw?.queriesByType || qRaw?.queries_by_type || undefined;
      const queriesByAssignee = qRaw?.queriesByAssignee || qRaw?.queries_by_assignee || undefined;
      const customersTotal = qStats.customersTotal ?? qRaw?.customers_total ?? 0;
      const customersNew30 = qStats.customersNew30 ?? qRaw?.customers_new_30 ?? 0;
      const queriesTotal = qStats.queriesTotal ?? qRaw?.queries_total ?? (Array.isArray(qQueries) ? qQueries.length : 0);
      const queriesNew30 = qStats.queriesNew30 ?? qRaw?.queries_new_30 ?? 0;
      const followupsToday = qStats.followupsToday ?? qRaw?.followups_today ?? (Array.isArray(qFollowups) ? qFollowups.length : 0);

      // Normalize Travel/Payments/Bookings side
      const tBookings = (tRaw?.bookings || []) as any[];
      const paymentsReceived = tRaw?.paymentsReceived || tRaw?.payments_received || [];
      const paymentsPending = tRaw?.paymentsPending || tRaw?.payments_pending || [];
      let bookingsByDate = tRaw?.bookingsByDate || tRaw?.bookings_by_date || [];
      if ((!bookingsByDate || bookingsByDate.length === 0) && Array.isArray(tBookings)) {
        const now = new Date(); const d7 = new Date(now); d7.setDate(now.getDate() - 6); d7.setHours(0,0,0,0);
        const dateKeys: string[] = []; for (let i=0;i<7;i++){ const d=new Date(d7); d.setDate(d7.getDate()+i); dateKeys.push(ymdLocal(d)); }
        const sums: Record<string, Record<string, number>> = {}; dateKeys.forEach(k=> sums[k] = {});
        tBookings.forEach((b: any) => {
          const when = b.created_at || b.updated_at || b.travel_start_date || b.start_at;
          const k = when ? ymdLocal(new Date(when)) : '';
          if (!sums[k]) return;
          const t = String(b.product_type || '').toLowerCase() || 'other';
          sums[k][t] = (sums[k][t] || 0) + (Number(b.total_price)||0);
        });
        bookingsByDate = dateKeys.flatMap(k => Object.entries(sums[k]).map(([t,amt]) => ({ date:k, type:t, amount:amt })));
      }
      const departures = tRaw?.departures || [];
      const tStats = tRaw?.stats || {};
      const bookedAmt30 = tStats.bookedAmt30 ?? tRaw?.booked_amt_30 ?? 0;
      const paidAmt30 = tStats.paidAmt30 ?? tRaw?.paid_amt_30 ?? 0;

      return {
        queries: qQueries || [],
        recentQueries,
        queriesOverTime,
        queriesByType,
        queriesByAssignee,
        followups: qFollowups || [],
        bookings: tBookings || [],
        paymentsReceived,
        paymentsPending,
        bookingsByDate,
        departures,
        stats: { customersTotal, customersNew30, queriesTotal, queriesNew30, followupsToday, bookedAmt30, paidAmt30 },
      };
    }
  } catch {}

  // 2) Fallback: compose from existing raw APIs (single-shot)
  const [queries, bookings, followups, customers] = await Promise.all([
    fetchQueries().catch(() => []),
    fetchBookings().catch(() => []),
    fetchFollowupsForToday().catch(() => []),
    fetchCustomers().catch(() => []),
  ]);

  const now = new Date();
  const d30 = new Date(now); d30.setDate(now.getDate() - 30);
  const d7 = new Date(now); d7.setDate(now.getDate() - 6); d7.setHours(0,0,0,0);

  const customersTotal = Array.isArray(customers) ? customers.length : 0;
  const customersNew30 = (Array.isArray(customers) ? customers : []).filter((c:any)=>{
    const dt = c.created_at ? new Date(c.created_at) : null; return dt && dt >= d30;
  }).length;

  // Queries
  const queriesTotal = Array.isArray(queries) ? queries.length : 0;
  const queriesNew30 = (Array.isArray(queries) ? queries : []).filter((q:any)=>{
    const dt = q.queryDate ? new Date(q.queryDate) : null; return dt && dt >= d30;
  }).length;
  const recentQueries = (Array.isArray(queries) ? queries : [])
    .slice()
    .sort((a:any,b:any)=> new Date(b.queryDate).getTime() - new Date(a.queryDate).getTime())
    .slice(0,7);
  // queries over last 30 days
  const qoBuckets: Record<string, number> = {};
  const labelsArr: string[] = [];
  const start30 = new Date(now); start30.setDate(now.getDate() - 29);
  for (let i=0;i<30;i++){ const d=new Date(start30); d.setDate(start30.getDate()+i); const k=ymdLocal(d); qoBuckets[k]=0; labelsArr.push(k); }
  (Array.isArray(queries)?queries:[]).forEach((q:any) => {
    const dq = q.queryDate ? new Date(q.queryDate) : null; if (!dq) return; const k = ymdLocal(dq); if (k in qoBuckets) qoBuckets[k] += 1;
  });
  const queriesOverTime = labelsArr.map(k => ({ date: k, count: qoBuckets[k] }));

  // queries by type & assignee (fallback composition)
  const queriesByType = (Array.isArray(queries)?queries:[]).reduce((acc: Record<string, number>, q: any) => {
    const t = q.type || 'Other';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const queriesByTypeArr = Object.keys(queriesByType).map(type => ({ type, count: queriesByType[type] }));
  const queriesByAssigneeMap = (Array.isArray(queries)?queries:[]).reduce((acc: Record<string, { assigneeId: string; assigneeName: string; count: number }>, q: any) => {
    const id = q.assignedTo?.id != null ? String(q.assignedTo.id) : 'unassigned';
    const name = q.assignedTo?.name || 'Unassigned';
    if (!acc[id]) acc[id] = { assigneeId: id, assigneeName: name, count: 0 };
    acc[id].count += 1;
    return acc;
  }, {} as Record<string, { assigneeId: string; assigneeName: string; count: number }>);
  const queriesByAssigneeArr = Object.values(queriesByAssigneeMap);

  // Bookings and payments
  const bs = Array.isArray(bookings) ? bookings : [];
  const in30 = bs.filter((b:any)=>{ const dt=b.created_at?new Date(b.created_at):null; return dt && dt>=d30; });
  const bookedAmt30 = in30.reduce((sum:number,b:any)=> sum + (Number(b.total_price)||0), 0);
  const paidAmt30 = in30.reduce((sum:number,b:any)=>{ const st=String(b.payment_status||b.status||'').toUpperCase(); const paid = st.includes('CAPTURE')||st.includes('PAID')||st.includes('SUCCESS'); return sum + (paid ? (Number(b.total_price)||0) : 0); }, 0);

  // Payments lists (received vs pending)
  const received: Array<{ date: string; customer: string; amount: number }> = [];
  const pending: Array<{ date: string; customer: string; amount: number }> = [];
  const fmt = (iso?: string) => { if (!iso) return '—'; const d = new Date(iso); return d.toISOString(); };
  bs.forEach((b:any)=>{
    const amt = Number(b.total_price||0);
    const name = b.customer?.name || '—';
    const date = fmt(b.updated_at || b.created_at);
    const st = String(b.payment_status || b.status || '').toUpperCase();
    const isRec = st.includes('CAPTURE')||st.includes('PAID')||st.includes('SUCCESS');
    (isRec ? received : pending).push({ date, customer: name, amount: amt });
  });
  received.sort((a,b)=> Date.parse(b.date) - Date.parse(a.date)); pending.sort((a,b)=> Date.parse(b.date) - Date.parse(a.date));

  // Bookings by date and type (last 7 days)
  const dateKeys: string[] = [];
  for (let i=0;i<7;i++){ const d=new Date(d7); d.setDate(d7.getDate()+i); dateKeys.push(ymdLocal(d)); }
  const bookingsByDate: Array<{ date: string; type: string; amount: number }> = [];
  const sums: Record<string, Record<string, number>> = {};
  dateKeys.forEach(k=> sums[k] = {});
  bs.forEach((b:any)=>{
    const k = ymdLocal(new Date(b.created_at || b.updated_at || b.travel_start_date || Date.now()));
    if (!sums[k]) return;
    const t = String(b.product_type || '').toLowerCase() || 'other';
    sums[k][t] = (sums[k][t] || 0) + (Number(b.total_price)||0);
  });
  dateKeys.forEach(k=> { Object.entries(sums[k]).forEach(([t,amt])=> bookingsByDate.push({ date:k, type:t, amount:amt })); });

  // Departures next 7 days
  const now0 = new Date(); now0.setHours(0,0,0,0);
  const end = new Date(now0); end.setDate(now0.getDate()+7);
  const departures = bs.filter(b => { const d=b.travel_start_date?new Date(b.travel_start_date):null; return d && d >= now0 && d <= end; })
    .slice(0,6)
    .map(b => ({ title: b.package?.title || '—', date: new Date(b.travel_start_date).toISOString(), customer: b.customer?.name || '—' }));

  return {
    queries: Array.isArray(queries) ? queries : [],
    recentQueries,
    queriesOverTime,
    queriesByType: queriesByTypeArr,
    queriesByAssignee: queriesByAssigneeArr,
    followups: Array.isArray(followups) ? followups : [],
    bookings: bs,
    paymentsReceived: received.slice(0,7),
    paymentsPending: pending.slice(0,7),
    bookingsByDate,
    departures,
    stats: { customersTotal, customersNew30, queriesTotal, queriesNew30, followupsToday: (Array.isArray(followups)?followups:[]).length, bookedAmt30, paidAmt30 },
  };
}
